import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  dbui_v1_TestAccessRequest,
  workspace_v1_DefaultConfig,
  workspace_v1_WorkspaceConfig,
} from '@antimatterhq/asl-api-ts-v1';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Input, InputProps } from '@antimatterhq/ui/components/input';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useField, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDebouncedCallback } from 'use-debounce';
import { cn } from '@/lib/utils';
import { useTestAccess } from '@/hooks/dbui/useTestAccess';
import { useCheckFormikError } from '@/hooks/formik/useCheckFormikError';
import { FormInput } from '@/components/form/input/FormInput';
import { useAsyncValidation } from '@/contexts/AsyncValidationContext';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';

type TableSchemaType = 'bronzeSchema' | 'silverSchema' | 'goldSchema';

type WorkspaceSchemaInfo = Pick<
  workspace_v1_DefaultConfig,
  'catalogName' | 'bronzeSchema' | 'silverSchema' | 'goldSchema'
>;

// Extract schema fields from datasources and global configs
const extractSchemaFields = (
  config:
    | Pick<
        workspace_v1_DefaultConfig,
        'catalogName' | 'bronzeSchema' | 'silverSchema' | 'goldSchema'
      >
    | null
    | undefined,
) => ({
  catalogName: config?.catalogName,
  bronzeSchema: config?.bronzeSchema,
  silverSchema: config?.silverSchema,
  goldSchema: config?.goldSchema,
});

const getWorkspaceSchemaInfo = (
  workspaceConfig?: workspace_v1_WorkspaceConfig,
): WorkspaceSchemaInfo => {
  const datasourcesConfig = workspaceConfig?.spec.datasources;

  const datasourceFields = extractSchemaFields(datasourcesConfig);

  // Merge fields, preferring datasources over global, and only if not already set
  return {
    catalogName: datasourceFields.catalogName ?? undefined,
    bronzeSchema: datasourceFields.bronzeSchema ?? undefined,
    silverSchema: datasourceFields.silverSchema ?? undefined,
    goldSchema: datasourceFields.goldSchema ?? undefined,
  };
};

export interface BaseUsePathValidationProps {
  value: string;
  constraint: dbui_v1_TestAccessRequest['constraint'];
  required?: boolean;
  onError?: (error: string | null) => void;
  debounceTime?: number;
  disabled?: boolean;
}

type UsePathValidationProps =
  | (BaseUsePathValidationProps & {
      validationType: 'table';
      tableSchema: TableSchemaType;
    })
  | (BaseUsePathValidationProps & {
      validationType: 'path';
      tableSchema?: never;
    });

const DEFAULT_DEBOUNCE_TIME = 500;

export function useDatabricksPathValidation(
  props: UsePathValidationProps & {
    onPendingChange?: (isPending: boolean) => void;
  },
) {
  const {
    value,
    constraint,
    validationType,
    onError,
    onPendingChange,
    debounceTime = DEFAULT_DEBOUNCE_TIME,
    disabled = false,
    required = false,
  } = props;
  const [isPending, setIsPending] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: workspaceConfig } = useGetWorkspaceConfig();
  const { mutateAsync: testAccessAsync } = useTestAccess();

  const getFullTableName = useCallback(
    (tableName: string, tableSchema: TableSchemaType) => {
      const schemaInfo = getWorkspaceSchemaInfo(workspaceConfig);
      const catalogName = schemaInfo?.catalogName;
      const schemaName = schemaInfo?.[tableSchema];

      if (!catalogName || !schemaName) {
        // If config is not available, return the table name as-is
        return tableName;
      }

      return `${catalogName}.${schemaName}.${tableName}`;
    },
    [workspaceConfig],
  );

  const clearValidation = useCallback(
    (pending: boolean = false) => {
      setIsPending(pending);
      onPendingChange?.(pending);
      setIsValid(null);
      setError(null);
      onError?.(null);
    },
    [onPendingChange, onError],
  );

  const validatePath = useCallback(
    async (inputValue: string) => {
      // Early return if disabled - no validation needed
      if (disabled) {
        clearValidation();
        return;
      }

      // Handle empty value case
      if (!inputValue) {
        if (required) {
          const requiredErrorMessage =
            validationType === 'table'
              ? 'Table name is required'
              : 'Path is required';

          setIsValid(false);
          setError(requiredErrorMessage);
          onError?.(requiredErrorMessage);
        } else {
          // Clear validation state for optional empty fields
          clearValidation();
        }
        return;
      }

      // Start validation process
      clearValidation(true);

      try {
        // Prepare validation request based on type
        const validationRequest =
          validationType === 'table' && props.tableSchema
            ? {
                table: getFullTableName(inputValue, props.tableSchema),
                constraint,
              }
            : { path: inputValue, constraint };

        const result = await testAccessAsync(validationRequest);

        // Handle validation result
        const isValidResult = result?.testSuccess ?? false;
        setIsValid(isValidResult);

        if (!isValidResult && result?.failureDetails) {
          setError(result.failureDetails);
          onError?.(result.failureDetails);
        } else {
          setError(null);
          onError?.(null);
        }
      } catch (err) {
        // Handle validation errors
        setIsValid(false);
        const errorMessage =
          err instanceof Error ? err.message : 'Validation failed';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        // Always reset pending state
        setIsPending(false);
        onPendingChange?.(false);
      }
    },
    [
      disabled,
      clearValidation,
      required,
      validationType,
      onError,
      props.tableSchema,
      getFullTableName,
      constraint,
      testAccessAsync,
      onPendingChange,
    ],
  );

  const debouncedValidatePath = useDebouncedCallback(
    validatePath,
    debounceTime,
  );

  useEffect(() => {
    debouncedValidatePath(value);

    return () => {
      debouncedValidatePath.cancel();
    };
  }, [value, debouncedValidatePath]);

  return { isPending, isValid, error };
}

interface BaseFormDatabricksPathAndTableValidatedInputProps
  extends Omit<
    InputProps,
    'value' | 'onChange' | 'onBlur' | 'name' | 'variant'
  > {
  name: string;
  constraint: dbui_v1_TestAccessRequest['constraint'];
  debounceTime?: number;
}

type FormDatabricksPathAndTableValidatedInputProps =
  | (BaseFormDatabricksPathAndTableValidatedInputProps & {
      validationType: 'table';
      tableSchema: 'bronzeSchema' | 'silverSchema' | 'goldSchema';
    })
  | (BaseFormDatabricksPathAndTableValidatedInputProps & {
      validationType: 'path';
      tableSchema?: never;
    });

export const FormDatabricksPathAndTableValidatedInput = (
  props: FormDatabricksPathAndTableValidatedInputProps,
) => {
  const {
    name,
    constraint,
    validationType,
    debounceTime,
    className,
    disabled,
    required,
    tableSchema,
    ...rest
  } = props;

  const { enableApiFieldValidation } = useFlags<FlagSet>();
  const [field] = useField<string>(name);
  const { isSubmitting, setErrors } = useFormikContext();
  const { error: formikError } = useCheckFormikError({ name });

  const { setError, clearError, setPending } = useAsyncValidation();

  const onError = useCallback(
    (error: unknown) => {
      if (enableApiFieldValidation) {
        setErrors({ [name]: error });
        if (error) {
          setError(name, error as string);
        } else {
          clearError(name);
        }
      }
    },
    [enableApiFieldValidation, name, setErrors, setError, clearError],
  );

  const onPendingChange = useCallback(
    (isPendingState: boolean) => {
      if (enableApiFieldValidation) {
        setPending(name, isPendingState);
      }
    },
    [enableApiFieldValidation, name, setPending],
  );

  const databricksPathValidationArgs = useMemo(() => {
    if (validationType === 'table') {
      return {
        value: enableApiFieldValidation ? field.value : '',
        constraint,
        required,
        validationType,
        debounceTime,
        onError,
        onPendingChange,
        tableSchema,
        disabled: disabled || isSubmitting,
      };
    }

    return {
      value: enableApiFieldValidation ? field.value : '',
      constraint,
      validationType,
      debounceTime,
      onError,
      onPendingChange,
      disabled: disabled || isSubmitting,
    };
  }, [
    validationType,
    enableApiFieldValidation,
    field.value,
    constraint,
    debounceTime,
    onError,
    onPendingChange,
    disabled,
    isSubmitting,
    required,
    tableSchema,
  ]);

  const {
    isPending,
    isValid,
    error: validationError,
  } = useDatabricksPathValidation(databricksPathValidationArgs);

  // If feature flag is off, just render FormInput
  if (!enableApiFieldValidation) {
    return (
      <FormInput
        name={name}
        disabled={disabled}
        className={className}
        {...rest}
      />
    );
  }

  const hasError =
    !!formikError ||
    (validationError !== null && !isPending) ||
    (isValid === false && !isPending);
  const isEffectivelyDisabled = disabled || isSubmitting;

  const variant = hasError ? 'error' : 'default';

  return (
    <div className="relative w-full max-w-form">
      <Input
        variant={variant}
        disabled={isEffectivelyDisabled}
        className={cn('max-w-form pr-10', className)}
        autoComplete="off"
        data-testid={`form-databricks-validated-input-${name}`}
        {...field}
        {...rest}
      />
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {isPending && (
          <Icon
            icon="lucide:loader-circle"
            className="h-4 w-4 animate-spin text-gray-400"
          />
        )}
        {isValid === true && !isPending && !formikError && (
          <Icon
            icon="lucide:check-circle"
            className={cn(
              'h-4 w-4 text-green-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
        {hasError && !isPending && (
          <Icon
            icon="lucide:octagon-alert"
            className={cn(
              'h-4 w-4',
              'text-red-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
      </div>
    </div>
  );
};
